import React, { useMemo } from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import {
  hooks,
  ProductLine,
  ApplicationThemeProvider,
  ApplicationNotificationsProvider,
} from '@adc/ui-core'
import configment from '@adc/configment'
import Header from '../header'
import { ApplicationContextProvider } from './context'
import ApplicationErrorBoundary from './errorBoundary'
import SharedPageMetadata from './sharedPageMetadata'
import { DEFAULT_CAPABILITIES } from './shell'

const FocusedAuctionApplicationShell = ({
  children,
  user,
  sspConfig,
  helmetConfig,
  featureFlags,
  capabilities, // see comment in ./shell.js for more info on capabilities
  theme = {
    productLine: ProductLine.AuctionCom,
    version: 1,
  },
  hideHeader = false,
}) => {
  hooks.useMountEffect(() => {
    document.body.setAttribute('data-app-state', 'ready')
    document.body.setAttribute('data-app-id', sspConfig.APP_NAME)
  })

  const productLineFromHost = hooks.useProductLineFromHost()

  const memoizedCapabilities = useMemo(() => {
    /**
     * Get accessibility options from configment per product line e.g smartsale, auctioncom.
     * in format of { equalweb: { site_key: 'site key', enabled: true } }
     */
    const accessibility =
      configment.get('accessibility', {})[productLineFromHost] || {}
    return _.merge(
      {},
      DEFAULT_CAPABILITIES,
      {
        accessibility,
      },
      capabilities
    )
  }, [capabilities, productLineFromHost])

  return (
    <ApplicationContextProvider
      sspConfig={sspConfig}
      user={user}
      featureFlags={featureFlags}
    >
      <ApplicationNotificationsProvider>
        <ApplicationErrorBoundary>
          <ApplicationThemeProvider theme={theme}>
            <SharedPageMetadata
              helmetConfig={helmetConfig}
              sspConfig={sspConfig}
              featureFlags={featureFlags}
              accessibilityOptions={memoizedCapabilities.accessibility}
            />
            {!hideHeader && <Header.LogoHeader isSticky />}
            {children}
          </ApplicationThemeProvider>
        </ApplicationErrorBoundary>
      </ApplicationNotificationsProvider>
    </ApplicationContextProvider>
  )
}

FocusedAuctionApplicationShell.propTypes = {
  user: PropTypes.object,
  sspConfig: PropTypes.object,
  helmetConfig: PropTypes.object,
  featureFlags: PropTypes.object,
  children: PropTypes.node.isRequired,
  capabilities: PropTypes.shape({
    accessibility: PropTypes.shape({
      equalweb: PropTypes.object,
    }),
  }),
  theme: PropTypes.object,
  hideHeader: PropTypes.bool,
}

export default FocusedAuctionApplicationShell
